import { Component, OnInit } from '@angular/core';
import { filter, firstValueFrom, map, mergeMap, of, catchError, tap } from 'rxjs';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { AppService, DCO, GetRuntimeAppSettingsRequest } from './app.service';
import { AlertService } from './common/alert.service';
import { LoadingService } from './common/loading.service';
import { AuthService } from './auth/services/auth.service';
import { SessionTimeoutService } from './auth/services/session-timeout.service';
import { TokenBootstrapService } from './auth/services/token-bootstrap.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public showNav = false;
  public userAccountDco: DCO = null;
  userEmail?: string;

  timeLeft = 0;           // seconds
  showCountdown = false;

  title = 'dentalbidirectional.ui';

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private titleService: Title,
    private sessionTimeout: SessionTimeoutService,
    private tokenBootstrapService: TokenBootstrapService
  ) { }

  async ngOnInit() {
    // If you need token bootstrap first, do it here
    // this.tokenBootstrapService.initializeTokenBootstrap();

    // Hydrate user once (fresh reloads won't have currentUser yet)
    const me = this.authService.isAuthenticated()
      ? this.authService.getUser()
      : await this.authService.getCurrentUser();

    if (me) {
      await this.initializeSessionTimeoutSettings();

      // Hook up countdown streams AFTER monitoring starts
      this.sessionTimeout.getTimeLeft$().subscribe(t => this.timeLeft = Math.ceil(t / 1000));
      this.sessionTimeout.getCountdownVisible$().subscribe(v => this.showCountdown = v);
    }

    // Handle title changes on every route change
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      mergeMap(route => route.data ?? [])
    ).subscribe(data => {
      const title = data['title'] || 'CareOregon DBD';
      this.titleService.setTitle(title);
    });
  }

  private async initializeSessionTimeoutSettings(): Promise<void> {
    const cacheKey = 'runtimeAppSettings:timeout';
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        const c = JSON.parse(cached) as { uiTimeoutMs: number; uiTimeoutWarningMs: number };
        const safeWarn = Math.min(c.uiTimeoutWarningMs, c.uiTimeoutMs - 1000);
        this.sessionTimeout.setSettings(c.uiTimeoutMs, safeWarn);
        this.sessionTimeout.startMonitoring();
        return;
      } catch {
        // corrupted cache → fall through to API
      }
    }

    const req: GetRuntimeAppSettingsRequest = { name: '' };

    await firstValueFrom(
      this.service.GetRuntimeAppSettings(req).pipe(
        map(res => {
          const get = (key: string, def: number) =>
            parseInt(res.settings?.find(s => s.name === key)?.value ?? '', 10) || def;

          const uiTimeoutMs = get('uiTimeoutMs', 15 * 60 * 1000);          // default 15m
          const uiTimeoutWarningMs = get('uiTimeoutWarningMs', 60 * 1000); // default 1m
          const safeWarn = Math.min(uiTimeoutWarningMs, uiTimeoutMs - 1000);

          this.sessionTimeout.setSettings(uiTimeoutMs, safeWarn);
          sessionStorage.setItem(cacheKey, JSON.stringify({
            uiTimeoutMs,
            uiTimeoutWarningMs: safeWarn
          }));
        }),
        catchError(err => {
          console.error('Failed to load runtime app settings', err);
          // Fall back to baked-in defaults inside SessionTimeoutService
          return of(void 0);
        }),
        tap(() => this.sessionTimeout.startMonitoring())
      )
    );
  }

  formattedTimeLeft(): string {
    const totalSeconds = this.timeLeft;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0'),
    ].join(':');
  }
}
